'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'src/contexts/AuthContext';
import { supabase } from 'src/lib/supabase';
import { useExtensionConfig } from './useUserConfig';
import type { Session } from '@supabase/supabase-js';

interface ExtensionStatus {
  isInstalled: boolean;
  isChecking: boolean;
  error: string | null;
}

export function useExtensionAuth() {
  const { user } = useAuth();
  const { getEffectiveExtensionId } = useExtensionConfig();
  const [session, setSession] = useState<Session | null>(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);
  const [extensionStatus, setExtensionStatus] = useState<ExtensionStatus>({
    isInstalled: false,
    isChecking: true,
    error: null
  });

  // Manage session state directly with Supabase
  useEffect(() => {
    let mounted = true;

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (mounted) {
          setSession(session);
          setIsSessionLoading(false);
          console.log('Initial session loaded:', session ? 'Found' : 'None');
        }
      } catch (error) {
        console.error('Error getting initial session:', error);
        if (mounted) {
          setIsSessionLoading(false);
        }
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session ? 'Session exists' : 'No session');
        if (mounted) {
          setSession(session);
          setIsSessionLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Get the extension ID from user config or environment
  const getExtensionId = useCallback(() => {
    return getEffectiveExtensionId();
  }, [getEffectiveExtensionId]);

  // Check if extension is installed
  const checkExtensionInstalled = useCallback(async (): Promise<boolean> => {
    try {
      const extensionId = getExtensionId();
      if (!extensionId) {
        console.warn('Extension ID not configured - NEXT_PUBLIC_EXTENSION_ID missing');
        return false;
      }

      return new Promise((resolve) => {
        // Check if we're in Chrome and chrome API is available
        if (typeof window === 'undefined' || !window.chrome?.runtime) {
          resolve(false);
          return;
        }

        // Send message to extension to check if it's installed
        window.chrome!.runtime!.sendMessage(
          extensionId,
          { type: 'CHECK_EXTENSION_STATUS' },
          (response) => {
            if (window.chrome!.runtime!.lastError) {
              console.log('Extension not installed/disabled:', window.chrome!.runtime!.lastError.message);
              resolve(false);
            } else {
              console.log('Extension detected:', response);
              resolve(response?.success === true);
            }
          }
        );

        // Timeout after 1 second
        setTimeout(() => resolve(false), 1000);
      });
    } catch (error) {
      console.error('Error checking extension:', error);
      return false;
    }
  }, [getExtensionId]);

  // Share session tokens with extension
  const shareTokensWithExtension = useCallback(async (): Promise<boolean> => {
    console.log('shareTokensWithExtension called - session:', session ? 'exists' : 'null', 'user:', user ? 'exists' : 'null');
    
    if (!session?.access_token) {
      console.log('No session access_token available');
      return false;
    }
    
    if (!user) {
      console.log('No user available');
      return false;
    }

    try {
      const extensionId = getExtensionId();
      console.log('Extension ID from env:', extensionId);
      
      if (!extensionId) {
        console.warn('Extension ID not configured - set NEXT_PUBLIC_EXTENSION_ID');
        return false;
      }

      // Check if we're in Chrome and chrome API is available
      if (typeof window === 'undefined') {
        console.log('Not in browser environment');
        return false;
      }
      
      if (!window.chrome?.runtime) {
        console.log('Chrome runtime not available');
        return false;
      }

      const payload = {
        accessToken: session.access_token,
        refreshToken: session.refresh_token || '',
        expiresAt: session.expires_at ? session.expires_at * 1000 : Date.now() + 3600000, // Convert to milliseconds
        user: user
      };

      console.log('Sending message to extension:', extensionId, 'with payload keys:', Object.keys(payload));

      return new Promise((resolve) => {
        window.chrome!.runtime!.sendMessage(
          extensionId,
          { 
            type: 'SHARE_SESSION_TOKEN',
            payload 
          },
          (response) => {
            console.log('Extension response received:', response);
            console.log('Chrome runtime lastError:', window.chrome!.runtime!.lastError);
            
            if (window.chrome!.runtime!.lastError) {
              console.error('Chrome runtime error:', window.chrome!.runtime!.lastError.message);
              resolve(false);
            } else {
              console.log('Tokens shared successfully:', response);
              resolve(response?.success === true);
            }
          }
        );

        // Timeout after 5 seconds
        setTimeout(() => {
          console.log('Extension message timeout - no response after 5 seconds');
          resolve(false);
        }, 5000);
      });
    } catch (error) {
      console.error('Error sharing tokens with extension:', error);
      return false;
    }
  }, [session, user, getExtensionId]);

  // Check extension status on mount and when session changes
  useEffect(() => {
    let isMounted = true;

    const checkExtension = async () => {
      if (!isMounted) return;
      
      setExtensionStatus(prev => ({ ...prev, isChecking: true, error: null }));

      try {
        const isInstalled = await checkExtensionInstalled();
        
        if (!isMounted) return;
        
        setExtensionStatus({
          isInstalled,
          isChecking: false,
          error: null
        });

        // If extension is installed and we have a session, share tokens
        if (isInstalled && session?.access_token && !isSessionLoading) {
          const shared = await shareTokensWithExtension();
          if (shared) {
            console.log('Session automatically shared with extension');
          }
        }
      } catch (error) {
        if (!isMounted) return;
        
        console.error('Extension check failed:', error);
        setExtensionStatus({
          isInstalled: false,
          isChecking: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    };

    // Only check if we're in a browser environment and session is not loading
    if (typeof window !== 'undefined' && !isSessionLoading) {
      checkExtension();
    }

    return () => {
      isMounted = false;
    };
  }, [session?.access_token, user?.id, isSessionLoading, checkExtensionInstalled, shareTokensWithExtension]);

  return {
    extensionStatus,
    shareTokensWithExtension,
    checkExtensionInstalled
  };
}